import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown, Input, Modal, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
    BuildingsIcon,
    CaretDown,
    Check,
    DotsThreeIcon,
    FloppyDiskIcon,
    LightningIcon,
    ListBulletsIcon,
    MagnifyingGlassIcon,
    SquaresFourIcon,
    X,
} from '@phosphor-icons/react';
import { addModulesToAllRoofsMaxFill } from '../../Three-JS-Viewer/Controllers/ModuleControl';
import {
    DashboardAiDesignsSourceBadge,
    type AiDesignsDataSource,
} from './DashboardAiDesignsSourceBadge';
import { DashboardSaveAiDesignsModal } from './DashboardSaveAiDesignsModal';

export type DashboardAiScenariosModuleBarProps = {
    theme: 'dark' | 'light';
    moduleList: any[];
    activeModule: any;
    onModuleChange?: (next: any) => void;
    sceneViewer: any;
    handleRunAi: () => void;
    aiLoading: boolean;
    runAiReady: boolean;
    roofCards: any[];
    handleSaveAiScenarios: (includeSnapshotsOnSave: boolean) => void | Promise<void>;
    savingScenarios: boolean;
    aiSavingProgress: number | null | undefined;
    setAltLayoutsCategory: (label: string | null) => void;
    setAltLayoutsModalOpen: (open: boolean) => void;
    setRoofSuggestionsModalOpen: (open: boolean) => void;
    geminiResultsEnabled?: boolean;
    aiRunElapsedMs?: number | null;
    onMoreLayoutsClick?: () => void;
    saveDesignsDisabled?: boolean;
    catalogViewMode?: 'table' | 'card';
    onCatalogViewModeChange?: (mode: 'table' | 'card') => void;
    showCatalogViewToggle?: boolean;
    aiDesignsDataSource?: AiDesignsDataSource;
    isLoadingSnapshots?: boolean;
};

export const DashboardAiScenariosModuleBar: React.FC<DashboardAiScenariosModuleBarProps> = ({
    theme,
    moduleList,
    activeModule,
    onModuleChange,
    sceneViewer,
    handleRunAi,
    aiLoading,
    runAiReady,
    roofCards,
    handleSaveAiScenarios,
    savingScenarios,
    aiSavingProgress,
    setAltLayoutsCategory,
    setAltLayoutsModalOpen,
    setRoofSuggestionsModalOpen,
    geminiResultsEnabled = false,
    aiRunElapsedMs,
    onMoreLayoutsClick,
    saveDesignsDisabled = false,
    catalogViewMode = 'card',
    onCatalogViewModeChange,
    showCatalogViewToggle = false,
    aiDesignsDataSource = null,
    isLoadingSnapshots = false,
}) => {
    const handleMoreLayoutsClick = useCallback(() => {
        if (onMoreLayoutsClick) {
            onMoreLayoutsClick();
            return;
        }
        setAltLayoutsCategory(null);
        setAltLayoutsModalOpen(true);
    }, [onMoreLayoutsClick, setAltLayoutsCategory, setAltLayoutsModalOpen]);

    const [modulePickerOpen, setModulePickerOpen] = useState(false);
    const [moduleSearch, setModuleSearch] = useState('');
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

    const openSaveConfirmModal = useCallback(() => {
        setSaveConfirmOpen(true);
        setOverflowOpen(false);
    }, []);

    const handleConfirmSave = useCallback(
        (includeImages: boolean) => {
            setSaveConfirmOpen(false);
            void handleSaveAiScenarios(includeImages);
        },
        [handleSaveAiScenarios],
    );

    useEffect(() => {
        if (!modulePickerOpen) {
            setModuleSearch('');
        }
    }, [modulePickerOpen]);

    const filteredModuleList = useMemo(() => {
        const q = moduleSearch.trim().toLowerCase();
        if (!q) return moduleList;
        return moduleList.filter((m: any) => {
            const label = String(m.text ?? '').toLowerCase();
            const watts = m.power != null && m.power !== '' ? String(m.power) : '';
            return label.includes(q) || watts.includes(q);
        });
    }, [moduleList, moduleSearch]);

    const applyModule = useCallback(
        (next: any) => {
            onModuleChange?.(next);
            if (
                sceneViewer &&
                next &&
                Array.isArray((sceneViewer as any).roofMeshArr) &&
                (sceneViewer as any).roofMeshArr.length > 0
            ) {
                try {
                    addModulesToAllRoofsMaxFill(sceneViewer);
                } catch {
                    // non-fatal
                }
            }
            setModulePickerOpen(false);
        },
        [onModuleChange, sceneViewer],
    );

    if (moduleList.length === 0) return null;

    const current = activeModule ?? moduleList[0];
    const currentLabel = current?.power ? `${current.text} · ${current.power}W` : current?.text ?? '';
    const moduleShortLabel = current?.text ?? 'Module';
    const elapsedDisplay =
        typeof aiRunElapsedMs === 'number' && Number.isFinite(aiRunElapsedMs) && aiRunElapsedMs > 0
            ? `${(aiRunElapsedMs / 1000).toFixed(1)}s`
            : null;
    const hasDesigns = Array.isArray(roofCards) && roofCards.length > 0;

    const runAiDisabled = !runAiReady || aiLoading;
    const runAiDisabledReason = !hasDesigns
        ? 'Create a layout first (place modules) to run AI designs.'
        : aiLoading
          ? 'AI designs are currently running. Please wait.'
          : !runAiReady
            ? 'Please wait—production values are still loading.'
            : null;

    const saveDisabled = !sceneViewer || savingScenarios || saveDesignsDisabled;

    const overflowMenuItems: MenuProps['items'] = hasDesigns
        ? [
              {
                  key: 'more-layouts',
                  label: 'More layouts',
                  icon: <SquaresFourIcon size={16} weight="duotone" aria-hidden />,
                  onClick: () => handleMoreLayoutsClick(),
              },
              {
                  key: 'save',
                  label: savingScenarios ? `Saving… ${aiSavingProgress ?? 0}%` : 'Save designs',
                  icon: <FloppyDiskIcon size={16} weight="fill" aria-hidden />,
                  disabled: saveDisabled,
                  onClick: () => {
                      if (!saveDisabled) openSaveConfirmModal();
                  },
              },
              {
                  key: 'gemini-results',
                  label: 'Show gemini results',
                  icon: <BuildingsIcon size={16} weight="duotone" aria-hidden />,
                  disabled: !geminiResultsEnabled,
                  onClick: () => setRoofSuggestionsModalOpen(true),
              },
          ]
        : [];

    const runControl = (
        <button
            type="button"
            className="editor-ai-command-strip__run"
            onClick={handleRunAi}
            disabled={runAiDisabled}
            aria-busy={aiLoading}
        >
            <LightningIcon size={15} weight="fill" aria-hidden className="editor-ai-command-strip__run-icon" />
            <span className="editor-ai-command-strip__run-text">
                <span className="editor-ai-command-strip__run-text--full">
                    {aiLoading ? 'Running…' : hasDesigns ? 'Re-run AI' : 'Run AI'}
                </span>
                <span className="editor-ai-command-strip__run-text--short" aria-hidden>
                    {aiLoading ? '…' : hasDesigns ? 'Re-run' : 'Run'}
                </span>
            </span>
        </button>
    );

    return (
        <>
            <div
                className={`editor-ai-command-strip editor-ai-command-strip--dock obd-theme-${theme}`}
                role="toolbar"
                aria-label="AI designs controls"
            >
                {aiDesignsDataSource ? (
                    <div className="editor-ai-command-strip__source">
                        <DashboardAiDesignsSourceBadge source={aiDesignsDataSource} theme={theme} />
                        {isLoadingSnapshots && aiDesignsDataSource === 'db' ? (
                            <span
                                className="obd-ai-designs-snapshot-loading-hint"
                                role="status"
                                aria-live="polite"
                            >
                                Loading snapshots from DB…
                            </span>
                        ) : null}
                    </div>
                ) : null}
                <div className="editor-ai-command-strip__lead">
                    {runAiDisabled && runAiDisabledReason ? (
                        <Tooltip placement="bottom" title={runAiDisabledReason} destroyOnHidden>
                            <span className="editor-ai-command-strip__run-wrap">{runControl}</span>
                        </Tooltip>
                    ) : (
                        runControl
                    )}

                    <button
                        type="button"
                        className="editor-ai-command-strip__module"
                        onClick={() => setModulePickerOpen(true)}
                        aria-label={`Module: ${currentLabel || 'Choose module'}`}
                        title={currentLabel || 'Choose module type'}
                        aria-haspopup="dialog"
                        aria-expanded={modulePickerOpen}
                    >
                        <span className="editor-ai-command-strip__module-label">{moduleShortLabel}</span>
                        {current?.power ? (
                            <span className="editor-ai-command-strip__module-watts">{current.power}W</span>
                        ) : null}
                        <CaretDown size={12} weight="bold" aria-hidden className="editor-ai-command-strip__module-caret" />
                    </button>
                </div>

                <div className="editor-ai-command-strip__trail-scroll">
                    <div className="editor-ai-command-strip__trail">
                    {hasDesigns && overflowMenuItems.length > 0 ? (
                        <Dropdown
                            menu={{ items: overflowMenuItems }}
                            trigger={['click']}
                            placement="bottomRight"
                            open={overflowOpen}
                            onOpenChange={setOverflowOpen}
                            destroyOnHidden
                        >
                            <button
                                type="button"
                                className="editor-ai-command-strip__icon-btn"
                                aria-label="More actions"
                                aria-haspopup="menu"
                                aria-expanded={overflowOpen}
                            >
                                <DotsThreeIcon size={18} weight="bold" aria-hidden />
                            </button>
                        </Dropdown>
                    ) : null}

                    {showCatalogViewToggle && onCatalogViewModeChange ? (
                        <div
                            className="editor-ai-command-strip__view"
                            role="group"
                            aria-label="Catalog layout"
                        >
                            <button
                                type="button"
                                className={`editor-ai-command-strip__view-btn${catalogViewMode === 'table' ? ' editor-ai-command-strip__view-btn--active' : ''}`}
                                onClick={() => onCatalogViewModeChange('table')}
                                aria-pressed={catalogViewMode === 'table'}
                                title="Table view"
                            >
                                <ListBulletsIcon size={16} weight={catalogViewMode === 'table' ? 'fill' : 'regular'} aria-hidden />
                            </button>
                            <button
                                type="button"
                                className={`editor-ai-command-strip__view-btn${catalogViewMode === 'card' ? ' editor-ai-command-strip__view-btn--active' : ''}`}
                                onClick={() => onCatalogViewModeChange('card')}
                                aria-pressed={catalogViewMode === 'card'}
                                title="Card view"
                            >
                                <SquaresFourIcon size={16} weight={catalogViewMode === 'card' ? 'fill' : 'regular'} aria-hidden />
                            </button>
                        </div>
                    ) : null}

                    {elapsedDisplay ? (
                        <span className="editor-ai-command-strip__elapsed" title="Last AI run duration">
                            {elapsedDisplay}
                        </span>
                    ) : null}
                    </div>
                </div>
            </div>

            <Modal
                open={modulePickerOpen}
                onCancel={() => setModulePickerOpen(false)}
                footer={null}
                width={440}
                destroyOnHidden
                title="Module type"
                closeIcon={<X size={16} weight="bold" aria-hidden />}
                className="editor-ai-module-picker-modal"
                rootClassName={`editor-ai-module-picker-modal-root obd-theme-${theme}`}
                centered
            >
                <p className="editor-ai-module-picker-modal__hint">
                    Select the active module for AI layouts and production estimates.
                </p>
                <Input
                    className="editor-ai-module-picker-modal__search"
                    allowClear
                    placeholder="Search by name or wattage…"
                    value={moduleSearch}
                    onChange={(e) => setModuleSearch(e.target.value)}
                    aria-label="Search modules"
                    prefix={
                        <MagnifyingGlassIcon
                            size={16}
                            weight="duotone"
                            className="editor-ai-module-picker-modal__search-icon"
                            aria-hidden
                        />
                    }
                />
                <ul className="editor-ai-module-picker-list" role="listbox" aria-label="Module types">
                    {filteredModuleList.length === 0 ? (
                        <li className="editor-ai-module-picker-empty" role="presentation">
                            No modules match your search.
                        </li>
                    ) : (
                        filteredModuleList.map((m: any, idx: number) => {
                            const selected = (activeModule?.text ?? moduleList[0]?.text) === m.text;
                            return (
                                <li key={`${m.text ?? 'mod'}::${m.power ?? ''}::${idx}`} role="presentation">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={selected}
                                        className={`editor-ai-module-picker-row ${selected ? 'editor-ai-module-picker-row--selected' : ''}`}
                                        onClick={() => applyModule(m)}
                                    >
                                        <span className="editor-ai-module-picker-row__text">
                                            <span className="editor-ai-module-picker-row__name">{m.text}</span>
                                            {m.power ? (
                                                <span className="editor-ai-module-picker-row__power">{m.power}W</span>
                                            ) : null}
                                        </span>
                                        {selected ? (
                                            <Check size={18} weight="bold" className="editor-ai-module-picker-row__check" aria-hidden />
                                        ) : (
                                            <span className="editor-ai-module-picker-row__check-placeholder" aria-hidden />
                                        )}
                                    </button>
                                </li>
                            );
                        })
                    )}
                </ul>
            </Modal>

            <DashboardSaveAiDesignsModal
                open={saveConfirmOpen}
                theme={theme}
                saving={savingScenarios}
                onCancel={() => setSaveConfirmOpen(false)}
                onConfirm={handleConfirmSave}
            />
        </>
    );
};
